import {
  Users,
  PenSquare,
  Home,
  Share2,
  Settings,
  Lightbulb,
  FileText,
  CheckCircle,
  Film,
  User,
  Sparkles,
  Folder,
  Layers,
  Mic,
  AudioLines,
  Pen,
  Calendar,
  Wand2,
  MessageSquare,
  MessageCircle,
  Scissors,
  Mail,
  Send,
  Phone,
  Zap,
  Bot,
  Shield,
  ClipboardList,
  Building2,
  CalendarClock,
  Receipt,
  Landmark,
  UserCog,
  CreditCard,
  FileWarning,
  BookOpen,
  BarChart3,
  FileCheck,
  FilePlus,
  FileEdit,
  ClipboardCheck,
  PhoneCall,
  Columns3,
} from 'lucide-react';
import type { ITopNavItem, ISidebarSection } from '@/@types/layout';

/** All possible top nav items — filtered by getTopNavItems() */
const ALL_TOP_NAV_ITEMS: ITopNavItem[] = [
  { id: 'leads', label: 'Leads', icon: Users, path: '/leads' },
  { id: 'creator', label: 'Creator', icon: PenSquare, path: '/post-writer' },
  { id: 'realestate', label: 'Real Estate', icon: Home, path: '/property/draft' },
  { id: 'insurance', label: 'Insurance', icon: Shield, path: '/insurance/policies' },
  { id: 'company', label: 'Company', icon: Building2, path: '/company/employees' },
  { id: 'control', label: 'Control', icon: BarChart3, path: '/control' },
];

/**
 * Get top nav items filtered by effective industry.
 * - Leads, Creator, Settings: always visible
 * - Real Estate: only for 'real_estate' industry
 * - Insurance: only for 'insurance' industry
 * - If isCompanyUser is true, only the company nav item is returned
 */
export function getTopNavItems(
  effectiveIndustry: string,
  isCompanyUser?: boolean,
  isSuperadmin?: boolean,
  _isOrgOwner?: boolean
): ITopNavItem[] {
  if (isCompanyUser) {
    return [];
  }
  return ALL_TOP_NAV_ITEMS.filter(item => {
    if (item.id === 'realestate') return effectiveIndustry === 'real_estate';
    if (item.id === 'insurance') return effectiveIndustry === 'insurance';
    if (item.id === 'company') return false;
    if (item.id === 'control') return isSuperadmin === true;
    return true;
  });
}

/** @deprecated Use getTopNavItems(industry) instead */
export const TOP_NAV_ITEMS = ALL_TOP_NAV_ITEMS;

const CREATOR_SIDEBAR: ISidebarSection[] = [
  {
    id: 'creator-main',
    title: null,
    items: [
      { label: 'Topics', icon: Lightbulb, path: '/post-writer' },
      { label: 'Posts', icon: FileText, path: '/posts' },
      { label: 'Reels', icon: Film, path: '/reels' },
      { label: 'Hairstyle', icon: Scissors, path: '/hairstyle' },
    ],
  },
  {
    id: 'creator-ai-avatar',
    title: 'AI AVATAR',
    items: [
      { label: 'Reference Persons', icon: User, path: '/ai-avatar/reference-persons' },
      { label: 'My Avatars', icon: Users, path: '/ai-avatar/my-avatars' },
    ],
  },
  {
    id: 'creator-photostudio',
    title: 'PHOTOSTUDIO',
    items: [
      { label: 'Full Control', icon: Sparkles, path: '/photostudio' },
      { label: 'AI Picks', icon: Wand2, path: '/ai-studio' },
      { label: 'Projects', icon: Folder, path: '/photostudio/projects' },
      { label: 'Elements', icon: Layers, path: '/photostudio/elements' },
    ],
  },
  {
    id: 'creator-voices',
    title: 'VOICES',
    items: [
      { label: 'Clone Voice', icon: Mic, path: '/voices/clone' },
      { label: 'My Voices', icon: AudioLines, path: '/voices/my-voices' },
    ],
  },
  {
    id: 'creator-tools',
    title: null,
    items: [
      { label: 'Writing Styles', icon: Pen, path: '/writing-styles' },
      { label: 'Calendar', icon: Calendar, path: '/scheduled' },
    ],
  },
];

const LEADS_SIDEBAR: ISidebarSection[] = [
  {
    id: 'leads-main',
    title: 'LEADS',
    items: [
      { label: 'All Leads', icon: Users, path: '/leads' },
      { label: 'Board', icon: Columns3, path: '/leads/board' },
      {
        label: 'Messages',
        icon: MessageCircle,
        path: '/messages',
        children: [
          { label: 'Gmail', icon: Mail, path: '/messages/gmail' },
          { label: 'Telegram', icon: Send, path: '/messages/telegram' },
          { label: 'WhatsApp', icon: Phone, path: '/messages/whatsapp' },
        ],
      },
    ],
  },
  {
    id: 'leads-tools',
    title: 'TOOLS',
    items: [{ label: 'Templates', icon: MessageSquare, path: '/templates' }],
  },
];

const REALESTATE_SIDEBAR: ISidebarSection[] = [
  {
    id: 'realestate-properties',
    title: 'PROPERTIES',
    items: [
      { label: 'Draft', icon: FileText, path: '/property/draft' },
      { label: 'Ready', icon: CheckCircle, path: '/property/ready' },
    ],
  },
  {
    id: 'realestate-tools',
    title: null,
    items: [
      { label: 'New Property', icon: Home, path: '/property/new' },
      { label: 'Video Settings', icon: Settings, path: '/property/video-settings' },
      { label: 'Knowledge Base', icon: BookOpen, path: '/real-estate/knowledge-base' },
    ],
  },
];

const CHAT_AGENTS_SECTION: ISidebarSection = {
  id: 'insurance-chat',
  title: 'CHAT AGENTS',
  items: [{ label: 'Chat History', icon: MessageSquare, path: '/insurance/chat-agents' }],
};

const VOICE_AGENTS_SECTION: ISidebarSection = {
  id: 'insurance-voice',
  title: 'VOICE AGENTS',
  items: [
    { label: 'Agents', icon: Bot, path: '/insurance/voice-agents' },
    { label: 'Call History', icon: PhoneCall, path: '/insurance/voice-agents/calls' },
    { label: 'Phone Numbers', icon: Phone, path: '/insurance/voice-agents/phones' },
    { label: 'SIP Trunks', icon: Settings, path: '/insurance/voice-agents/sip' },
  ],
};

const INSURANCE_SIDEBAR: ISidebarSection[] = [
  {
    id: 'insurance-main',
    title: 'INSURANCE',
    items: [
      { label: 'Policies', icon: ClipboardList, path: '/insurance/policies' },
      { label: 'Drafts', icon: FileEdit, path: '/insurance/drafts' },
      { label: 'Renewal', icon: CalendarClock, path: '/insurance/renewal' },
      { label: 'Customers', icon: Users, path: '/customers' },
      { label: 'Companies', icon: Building2, path: '/insurance/companies' },
      { label: 'Invoices', icon: Receipt, path: '/insurance/invoices' },
      { label: 'Payments', icon: CreditCard, path: '/insurance/payments' },
      { label: 'Claims', icon: FileWarning, path: '/insurance/claims' },
      { label: 'Pre-authorizations', icon: ClipboardCheck, path: '/insurance/pre-authorizations' },
      { label: 'Claim Forms', icon: FilePlus, path: '/insurance/claim-forms' },
      { label: 'Bank Accounts', icon: Landmark, path: '/insurance/bank-accounts' },
      { label: 'Insurance Types', icon: Shield, path: '/insurance/types' },
      { label: 'Providers', icon: Building2, path: '/insurance/providers' },
      { label: 'Benefit Plans', icon: FileCheck, path: '/insurance/benefit-plans' },
      { label: 'Knowledge Base', icon: BookOpen, path: '/insurance/knowledge-base' },
      { label: 'Settings', icon: Settings, path: '/insurance/settings' },
    ],
  },
  CHAT_AGENTS_SECTION,
  VOICE_AGENTS_SECTION,
];

const COMPANY_MANAGEMENT_SECTION: ISidebarSection = {
  id: 'company-main',
  title: 'COMPANY',
  items: [
    { label: 'Employees', icon: Users, path: '/company/employees' },
    { label: 'Policies', icon: ClipboardList, path: '/company/policies' },
    { label: 'Invoices', icon: Receipt, path: '/company/invoices' },
    { label: 'Claims', icon: FileWarning, path: '/company/claims' },
    { label: 'Managers', icon: UserCog, path: '/company/managers' },
    { label: 'Settings', icon: Settings, path: '/company/settings' },
  ],
};

const COMPANY_MY_INSURANCE_SECTION: ISidebarSection = {
  id: 'company-my-insurance',
  title: 'MY INSURANCE',
  items: [
    { label: 'My Policies', icon: Shield, path: '/company/my-insurance' },
    { label: 'Apply for Policy', icon: FilePlus, path: '/company/my-insurance/apply/new' },
    { label: 'My Applications', icon: FileEdit, path: '/company/my-insurance/applications' },
    { label: 'My Claims', icon: FileWarning, path: '/company/my-insurance/claims' },
    {
      label: 'Pre-authorizations',
      icon: ClipboardCheck,
      path: '/company/my-insurance/pre-authorizations',
    },
  ],
};

function getCompanySidebar(companyRoles: string[]): ISidebarSection[] {
  const isManager =
    companyRoles.includes('admin') ||
    companyRoles.includes('manager') ||
    companyRoles.includes('accountant');
  if (isManager) {
    return [COMPANY_MANAGEMENT_SECTION, COMPANY_MY_INSURANCE_SECTION];
  }
  // Employees only see their own insurance
  return [COMPANY_MY_INSURANCE_SECTION];
}

const CONNECTED_SIDEBAR: ISidebarSection[] = [
  {
    id: 'connected-accounts',
    title: 'ACCOUNTS',
    items: [{ label: 'Connected Accounts', icon: Share2, path: '/connected-accounts' }],
  },
  {
    id: 'connected-settings',
    title: 'SETTINGS',
    items: [
      { label: 'Business Settings', icon: Settings, path: '/business-settings' },
      { label: 'Organisation', icon: Users, path: '/settings/organisation' },
      { label: 'AI Models', icon: Sparkles, path: '/ai-models' },
      { label: 'AI Replies', icon: Bot, path: '/settings/ai-replies' },
      { label: 'Automation', icon: Zap, path: '/automation' },
    ],
  },
];

const CONTROL_SIDEBAR: ISidebarSection[] = [
  {
    id: 'control-main',
    title: 'CONTROL',
    items: [
      { label: 'Dashboard', icon: BarChart3, path: '/control' },
      { label: 'Users', icon: Users, path: '/control/users' },
      { label: 'Organisations', icon: Building2, path: '/control/organisations' },
      { label: 'Policy Templates', icon: ClipboardList, path: '/control/templates' },
      { label: 'Audit Log', icon: FileText, path: '/control/audit' },
    ],
  },
  {
    id: 'control-analytics',
    title: 'ANALYTICS',
    items: [{ label: 'Policy Stats', icon: Shield, path: '/control/policies' }],
  },
];

/**
 * Get sidebar sections for a given top nav section ID
 */
export function getSidebarForSection(
  sectionId: string,
  companyRoles?: string[]
): ISidebarSection[] {
  switch (sectionId) {
    case 'leads':
      return LEADS_SIDEBAR;
    case 'creator':
      return CREATOR_SIDEBAR;
    case 'realestate':
      return REALESTATE_SIDEBAR;
    case 'insurance':
      return INSURANCE_SIDEBAR;
    case 'connected':
      return CONNECTED_SIDEBAR;
    case 'company':
      return getCompanySidebar(companyRoles ?? []);
    case 'control':
      return CONTROL_SIDEBAR;
    default:
      return CREATOR_SIDEBAR;
  }
}

/**
 * Determine active section from current pathname
 */
export function getActiveSectionFromPath(pathname: string): string {
  if (pathname.startsWith('/control')) return 'control';
  if (pathname.startsWith('/company')) return 'company';
  if (
    pathname.startsWith('/leads') ||
    pathname.startsWith('/templates') ||
    pathname.startsWith('/messages')
  )
    return 'leads';
  if (pathname.startsWith('/property') || pathname.startsWith('/real-estate')) return 'realestate';
  if (
    pathname.startsWith('/insurance') ||
    pathname.startsWith('/customers') ||
    pathname.startsWith('/my-insurance')
  )
    return 'insurance';
  if (
    pathname.startsWith('/connected') ||
    pathname.startsWith('/business-settings') ||
    pathname.startsWith('/settings') ||
    pathname.startsWith('/ai-models') ||
    pathname.startsWith('/automation')
  )
    return 'connected';
  // Default to creator for all other routes
  return 'creator';
}
